import type { FC, ReactNode } from 'react'
import type { AxisFormat, Scale } from '@/components/charts/chart-utils'

import { cn } from '@/lib/utils'
import { formatAxisValue, topPercent } from '@/components/charts/chart-utils'

interface ChartFrameProps {
    height: number
    scale: Scale
    axisFormat: AxisFormat
    xAxis: ReactNode
    children: ReactNode
    emphasizeZero?: boolean
    className?: string
}

// Shared plot frame: y axis labels, recessive gridlines, the plot area and the x axis band.
// The plot area sits at top 12px, right 8px, bottom 28px, left 56px.
export const ChartFrame: FC<ChartFrameProps> = ({
    height,
    scale,
    axisFormat,
    xAxis,
    children,
    emphasizeZero = false,
    className
}): ReactNode => {
    return (
        <div className={cn('relative w-full select-none', className)} style={{ height }}>
            <div className='absolute top-3 bottom-7 left-0 w-12' aria-hidden='true'>
                {scale.ticks.map((tick) => (
                    <span
                        key={tick}
                        className='absolute right-0 -translate-y-1/2 text-[11px] leading-none whitespace-nowrap text-muted-foreground tabular-nums'
                        style={{ top: `${topPercent(tick, scale)}%` }}
                    >
                        {formatAxisValue(tick, scale, axisFormat)}
                    </span>
                ))}
            </div>

            <div className='absolute top-3 right-2 bottom-7 left-14'>
                {scale.ticks.map((tick) => (
                    <div
                        key={tick}
                        className={cn(
                            'pointer-events-none absolute inset-x-0 border-t',
                            emphasizeZero && tick === 0 ? 'border-foreground/25' : 'border-border/80',
                            !(emphasizeZero && tick === 0) && tick !== scale.min && 'border-dashed'
                        )}
                        style={{ top: `${topPercent(tick, scale)}%` }}
                        aria-hidden='true'
                    />
                ))}

                {children}
            </div>

            <div className='absolute right-2 bottom-0 left-14 h-6' aria-hidden='true'>
                {xAxis}
            </div>
        </div>
    )
}
